/**
 * useAppConfig Hook
 *
 * Listens to the global app configuration document for system-wide settings.
 * Used for features that affect all users simultaneously.
 *
 * Features:
 * - Force reload: Trigger all clients to refresh (after deployments)
 * - Maintenance mode: Show full-screen overlay blocking user interaction
 * - Future: Feature flags, min app version, etc.
 *
 * The force reload works by comparing:
 * - `forceReloadAfter` timestamp in Firestore
 * - `appLoadTime` when this session started
 * If forceReloadAfter > appLoadTime, the app reloads.
 *
 * Usage:
 *   // In AuthenticatedLayout (runs once per session)
 *   const { maintenanceMode, maintenanceMessage } = useAppConfig();
 */

import { useState, useRef, useCallback } from 'react';
import { useFirebaseStore } from '../store/firebaseStore';
import { useFirestoreDocument } from './firestore';
import { logger } from '../utils/logger';
import type { DocumentData, Timestamp } from 'firebase/firestore';

/** App configuration document structure */
interface AppConfig {
  /** Timestamp after which all clients should reload */
  forceReloadAfter?: Timestamp | null;
  /** Whether the app is in maintenance mode */
  maintenanceMode?: boolean;
  /** Message to show during maintenance */
  maintenanceMessage?: string;
  /** Minimum required app version */
  minAppVersion?: string;
}

/** Return type for useAppConfig hook */
export interface UseAppConfigReturn {
  /** Whether maintenance mode is active */
  maintenanceMode: boolean;
  /** Custom message to display during maintenance */
  maintenanceMessage: string;
}

/** Document path for app settings */
const APP_SETTINGS_PATH = 'config/appSettings';

/**
 * Parse app config document
 */
function parseAppConfig(data: DocumentData): AppConfig {
  return {
    forceReloadAfter: data.forceReloadAfter || null,
    maintenanceMode: data.maintenanceMode || false,
    maintenanceMessage: data.maintenanceMessage || '',
    minAppVersion: data.minAppVersion || '',
  };
}

/**
 * Hook to listen to app configuration and handle system-wide features
 *
 * @param enabled - Only activate listener when true (default: true). Pass isConnected
 *                  to prevent false "not found" warnings before Firebase auth completes.
 */
export const useAppConfig = (enabled: boolean = true): UseAppConfigReturn => {
  const db = useFirebaseStore((state) => state.db);

  // Maintenance mode state
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');

  // Capture when this session started (once, never changes)
  const appLoadTime = useRef<number>(Date.now());

  // Track if we've already triggered a reload (prevent loops)
  const hasTriggeredReload = useRef<boolean>(false);

  // Handle config changes
  const handleConfigLoaded = useCallback(
    (config: AppConfig | null) => {
      if (!config) {
        logger.log('[AppConfig] No config document found');
        setMaintenanceMode(false);
        setMaintenanceMessage('');
        return;
      }

      // Update maintenance mode state
      setMaintenanceMode(config.maintenanceMode || false);
      setMaintenanceMessage(config.maintenanceMessage || '');

      // Check for force reload
      if (
        config.forceReloadAfter &&
        !hasTriggeredReload.current
      ) {
        const forceReloadTime = config.forceReloadAfter.toMillis();

        if (forceReloadTime > appLoadTime.current) {
          logger.log('[AppConfig] Force reload triggered - reloading page');
          hasTriggeredReload.current = true;

          // Small delay to ensure any pending operations complete
          setTimeout(() => {
            window.location.reload();
          }, 100);
        }
      }
    },
    []
  );

  // Handle errors
  const handleError = useCallback(
    (errorMessage: string | null) => {
      if (errorMessage) {
        // Don't log as error - config doc might not exist yet
        logger.log('[AppConfig] Could not load config:', errorMessage);
      }
    },
    []
  );

  // Set up listener
  // Only enable when `enabled` prop is true (prevents false warnings before auth)
  useFirestoreDocument<AppConfig>({
    db,
    documentPath: APP_SETTINGS_PATH,
    parseDocument: parseAppConfig,
    onDataLoaded: handleConfigLoaded,
    onError: handleError,
    enabled,
  });

  return {
    maintenanceMode,
    maintenanceMessage,
  };
};

export default useAppConfig;
